// ───────────────────────────────────────────────────────────────
// Page d'inscription — création de compte + société de démo
// ────────────────────────────────────────────────────────────────
import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";
import { CONTACT_EMAIL } from "../config/legal";
import Chatbot from "../components/Chatbot";
import "../styles/design-tokens.css";

export default function Register({ onGoToLogin }) {
  const { register } = useAuth();
  const [name, setName]                 = useState("");
  const [companyName, setCompanyName]   = useState("");
  const [email, setEmail]               = useState("");
  const [password, setPassword]         = useState("");
  const [phone, setPhone]               = useState("");
  const [companySize, setCompanySize]   = useState("");
  const [jobTitle, setJobTitle]         = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [cguAccepted, setCguAccepted]   = useState(false);
  const [error, setError]               = useState("");
  const [loading, setLoading]           = useState(false);
  // Inscriptions fermées par l'interrupteur serveur (PUBLIC_REGISTRATION_OPEN=false)
  const [registrationClosed, setRegistrationClosed] = useState(false);

  useEffect(() => {
    api.auth.registrationStatus()
      .then(s => { if (s && s.open === false) setRegistrationClosed(true); })
      .catch(() => {}); // en cas d'erreur, le serveur refusera de toute façon l'inscription si elle est fermée
  }, []);

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");

    if (password.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (companyName.trim().length < 2) {
      setError("Le nom de société doit contenir au moins 2 caractères.");
      return;
    }
    if (!phone.trim()) {
      setError("Le téléphone est requis.");
      return;
    }
    if (!companySize) {
      setError("La taille de l'entreprise est requise.");
      return;
    }
    if (!cguAccepted) {
      setError("Vous devez accepter les conditions générales d'utilisation.");
      return;
    }

    setLoading(true);
    try {
      await register({
        email,
        password,
        name,
        company_name: companyName,
        phone,
        company_size: companySize,
        job_title: jobTitle || undefined,
        marketing_consent: marketingConsent,
        cgu_accepted: cguAccepted,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    fontFamily: "Inter, sans-serif",
    width: "100%", padding: "10px 12px", background: "var(--ink)",
    border: "0.5px solid var(--hairline)", borderRadius: 8, color: "var(--text)",
    fontSize: 13, outline: "none", boxSizing: "border-box",
  };
  const labelStyle = { fontFamily: "Manrope, sans-serif", fontSize: 12, color: "var(--text-muted)", display: "block", marginBottom: 6 };
  const linkStyle = { color: "var(--brand)", textDecoration: "underline" };

  return (
    <div className="exp-public" style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: "linear-gradient(135deg, var(--ink) 0%, var(--surface) 55%, var(--surface-2) 100%)",
    }}>
      <Chatbot sourcePage="register" />
      <div style={{ width: "100%", maxWidth: 400, padding: "0 16px" }}>

        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <img src="/assets/logo-icon-execpro360.png" alt="ExecPro360" style={{ width: 40, height: 40, marginBottom: 12, objectFit: "contain" }} />
          <div style={{ fontFamily: "Manrope, sans-serif", fontSize: 22, fontWeight: 800, color: "var(--text)" }}>ExecPro360</div>
          <div style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>
            Pilotage financier & opérationnel
          </div>
        </div>

        {/* Carte d'inscription */}
        <div style={{
          background: "var(--surface)", border: "0.5px solid var(--hairline)",
          borderRadius: 16, padding: "28px 24px",
        }}>
          <h2 style={{ fontFamily: "Manrope, sans-serif", fontSize: 16, fontWeight: 700, color: "var(--text)", margin: "0 0 6px" }}>
            Essai gratuit
          </h2>
          <p style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "var(--text-muted)", margin: "0 0 20px" }}>
            Créez votre compte et accédez immédiatement à votre société de démonstration.
          </p>

          {error && (
            <div style={{
              fontFamily: "Inter, sans-serif",
              padding: "10px 12px", background: "#3b1c1c", border: "0.5px solid #e24b4a",
              borderRadius: 8, fontSize: 12, color: "#fca5a5", marginBottom: 16,
            }}>
              ⚠️ {error}
            </div>
          )}

          {registrationClosed ? (
            <div role="status" style={{
              fontFamily: "Inter, sans-serif", padding: "14px 16px", background: "var(--ink)",
              border: "0.5px solid var(--hairline)", borderRadius: 10, fontSize: 13, color: "var(--text)", lineHeight: 1.6,
            }}>
              Inscriptions bientôt rouvertes, contactez{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: "var(--brand)", textDecoration: "underline" }}>{CONTACT_EMAIL}</a>
            </div>
          ) : (
          <form onSubmit={handleRegister}>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>
                Votre nom
              </label>
              <input
                type="text" required value={name} onChange={e => setName(e.target.value)}
                placeholder="Jean Dupont" autoFocus
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>
                Nom de la société
              </label>
              <input
                type="text" required value={companyName} onChange={e => setCompanyName(e.target.value)}
                placeholder="Ma Société"
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>
                Email professionnel
              </label>
              <input
                type="email" required value={email} onChange={e => setEmail(e.target.value)}
                placeholder="jean.dupont@masociete.fr"
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>
                Téléphone
              </label>
              <input
                type="tel" required value={phone} onChange={e => setPhone(e.target.value)}
                placeholder="06 12 34 56 78"
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>
                Taille de l'entreprise
              </label>
              <select
                required value={companySize} onChange={e => setCompanySize(e.target.value)}
                style={inputStyle}
              >
                <option value="">Sélectionner…</option>
                <option value="1-9">1 à 9 salariés</option>
                <option value="10-49">10 à 49 salariés</option>
                <option value="50+">50 salariés ou plus</option>
              </select>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>
                Fonction <span style={{ color: "var(--text-faint)" }}>(optionnel)</span>
              </label>
              <select
                value={jobTitle} onChange={e => setJobTitle(e.target.value)}
                style={inputStyle}
              >
                <option value="">Sélectionner…</option>
                <option value="dirigeant">Dirigeant</option>
                <option value="comptable_daf">Comptable / DAF</option>
                <option value="autre">Autre</option>
              </select>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>
                Mot de passe
              </label>
              <input
                type="password" required value={password} onChange={e => setPassword(e.target.value)}
                placeholder="8 caractères minimum" minLength={8}
                style={inputStyle}
              />
            </div>

            <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 12 }}>
              <input
                id="cgu" type="checkbox" required checked={cguAccepted}
                onChange={e => setCguAccepted(e.target.checked)}
                style={{ width: 14, height: 14, marginTop: 2, accentColor: "var(--brand)", cursor: "pointer" }}
              />
              <label htmlFor="cgu" style={{ fontFamily: "Inter, sans-serif", fontSize: 11, color: "var(--text-muted)", cursor: "pointer", lineHeight: 1.5 }}>
                J'accepte les{" "}
                <a href="/cgu" target="_blank" rel="noopener noreferrer" style={linkStyle}>conditions générales d'utilisation</a>
                {" "}et j'ai pris connaissance de la{" "}
                <a href="/confidentialite" target="_blank" rel="noopener noreferrer" style={linkStyle}>politique de confidentialité</a>.
              </label>
            </div>

            <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 20 }}>
              <input
                id="marketing" type="checkbox" checked={marketingConsent}
                onChange={e => setMarketingConsent(e.target.checked)}
                style={{ width: 14, height: 14, marginTop: 2, accentColor: "var(--brand)", cursor: "pointer" }}
              />
              <label htmlFor="marketing" style={{ fontFamily: "Inter, sans-serif", fontSize: 11, color: "var(--text-muted)", cursor: "pointer", lineHeight: 1.5 }}>
                J'accepte de recevoir des communications de ExecPro360 (actualités, offres). Vous pouvez vous désinscrire à tout moment. Consultez notre{" "}
                <a href="/confidentialite" target="_blank" rel="noopener noreferrer" style={linkStyle}>politique de confidentialité</a>.
              </label>
            </div>

            <button
              type="submit" disabled={loading}
              style={{
                fontFamily: "Manrope, sans-serif",
                width: "100%", padding: "11px", background: loading ? "var(--brand-hover)" : "var(--brand)",
                border: "none", borderRadius: 8, color: "#fff",
                fontSize: 13, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer",
                transition: "background 0.2s",
              }}
            >
              {loading ? "Création en cours..." : "Démarrer l'essai gratuit"}
            </button>
          </form>
          )}

          <div style={{ fontFamily: "Inter, sans-serif", textAlign: "center", marginTop: 16, fontSize: 12, color: "var(--text-muted)" }}>
            Déjà un compte ?{" "}
            <span
              style={{ fontFamily: "Manrope, sans-serif", color: "var(--brand)", cursor: "pointer", fontWeight: 600 }}
              onClick={onGoToLogin}
            >Se connecter</span>
          </div>
        </div>

        <div style={{ fontFamily: "Inter, sans-serif", textAlign: "center", marginTop: 20, fontSize: 11, color: "var(--text-faint)" }}>
          🔒 TLS 1.3 · RGPD · Chiffrement AES-256 · Sans carte bancaire
        </div>
      </div>
    </div>
  );
}
